/* eslint-disable no-use-before-define, no-nested-ternary, no-restricted-syntax, no-await-in-loop, no-plusplus, no-continue, no-return-assign, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions, jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/control-has-associated-label */
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Plus,
  Dices,
  ChevronDown,
  ArrowLeft,
  X,
  RotateCw,
  Camera,
  Sparkles,
} from 'lucide-react';
import {
  FlavorDetails,
  FlavorRepository,
  MixRepository,
  BrandRepository,
  DbBrand,
  getFlavorColor,
} from '../repositories/LocalDatabase';
import RadarChart from './RadarChart';
import { showHUD } from './NotificationHUD';
import QrScannerModal from './QrScannerModal';
import RecipeCardPopup from './RecipeCardPopup';
import { mapDbMixToShishaMix } from '../repositories/mapper';
import { ShishaMix } from '../types';

interface FormFlavorItem {
  id: string;
  isCustom: boolean;
  flavorId: string;
  brandName: string;
  flavorName: string;
  ratio: string;
  searchText: string;
  isDropdownOpen: boolean;
  isRequired?: boolean;
  isExcluded?: boolean;
}

export default function CreateRecipe() {
  const navigate = useNavigate();
  const location = useLocation();

  // State
  const [recipeName, setRecipeName] = useState('');
  const [flavors, setFlavors] = useState<FormFlavorItem[]>([]);
  const [allFlavors, setAllFlavors] = useState<FlavorDetails[]>([]);
  const [ownedFlavors, setOwnedFlavors] = useState<FlavorDetails[]>([]);
  const [allBrands, setAllBrands] = useState<DbBrand[]>([]);

  // Modals state
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showRandomModal, setShowRandomModal] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [scannedMixData, setScannedMixData] = useState<ShishaMix | null>(null);
  const [isFirstVisit, setIsFirstVisit] = useState(false);

  const [editingMixId, setEditingMixId] = useState<string | null>(null);

  // Random generate data
  const [randomFlavors, setRandomFlavors] = useState<
    {
      id: string;
      brand_name: string;
      name: string;
      ratio: number;
      color: string;
      taste_vector: FlavorDetails['taste_vector'];
    }[]
  >([]);
  const [randomTasteVector, setRandomTasteVector] = useState({
    sweetness: 0,
    sourness: 0,
    coolness: 0,
    fruitiness: 0,
  });
  const [lastRandomPool, setLastRandomPool] = useState<'all' | 'owned'>('all');

  // Initialize
  useEffect(() => {
    const state = location.state as any;
    const scannedText = state?.scannedText;

    // loadData完了後にスキャン処理や編集データ読み込みを実行する
    const init = async () => {
      const loadedFlavors = await loadData();
      if (state?.editMix) {
        const editMix = state.editMix;
        setEditingMixId(editMix.id);
        setRecipeName(editMix.title);
        const newFlavors = editMix.flavors.map((fl: any, idx: number) => {
          const knownFlavor = loadedFlavors.find((f) => f.brand_name === fl.brand && f.name === fl.name);
          return {
            id: `row-${idx}-${Date.now()}`,
            isCustom: !knownFlavor,
            flavorId: knownFlavor ? knownFlavor.id : '',
            brandName: fl.brand,
            flavorName: fl.name,
            ratio: String(fl.ratio),
            searchText: knownFlavor ? `${knownFlavor.brand_name} - ${knownFlavor.name}` : `${fl.brand} - ${fl.name}`,
            isDropdownOpen: false,
          };
        });
        setFlavors(newFlavors);
        window.history.replaceState({}, document.title);
      } else if (scannedText) {
        // データロード完了後に少し待ってからスキャン処理
        setTimeout(() => {
          handleScan(scannedText);
        }, 100);
        window.history.replaceState({}, document.title);
      }
    };

    const hasVisited = localStorage.getItem('hf_has_visited_create_recipe');
    if (!hasVisited) {
      setIsFirstVisit(true);
      const timer = setTimeout(() => {
        setIsFirstVisit(false);
        localStorage.setItem('hf_has_visited_create_recipe', 'true');
      }, 4000);
      init();
      return () => clearTimeout(timer);
    }
    init();
  }, [location.state]);

  const loadData = async () => {
    const list = await FlavorRepository.getAll();
    const brands = await BrandRepository.getAll();
    setAllFlavors(list);
    setOwnedFlavors(list.filter((f) => f.owned));
    setAllBrands(brands);

    // Initialize with 4 flavor rows
    setFlavors(
      Array.from({ length: 4 }).map((_, idx) => ({
        id: `row-${idx}-${Date.now()}`,
        isCustom: false,
        flavorId: '',
        brandName: '',
        flavorName: '',
        ratio: idx === 0 ? '40' : idx === 1 ? '30' : idx === 2 ? '20' : '10',
        searchText: '',
        isDropdownOpen: false,
      })),
    );
    return list;
  };

  const handleScan = async (decodedText: string) => {
    setShowScanner(false);
    
    // Check if it's a Flavor QR
    const flavorMatch = decodedText.match(/(?:flavors|f)\/([a-zA-Z0-9_-]+)/);
    let isFlavorJson = false;
    try {
      const data = JSON.parse(decodedText);
      // If it has flavor properties but not mix properties
      if (data && (data.name || data.flavorName || data.brand_name) && !data.flavors) {
        isFlavorJson = true;
      }
    } catch(e) {}

    if (flavorMatch || isFlavorJson) {
      showHUD({ type: 'success', message: 'フレーバーQRを検出しました。\n登録画面へ移動します。' });
      navigate('/flavors', { state: { openAddModal: true, scannedText: decodedText } });
      return;
    }

    // JSON形式のミックスカード判定（{ flavors[] } または { name, flavors[] }）
    try {
      const data = JSON.parse(decodedText);
      const mixFlavors = data.f || data.flavors;
      if (data && Array.isArray(mixFlavors) && mixFlavors.length > 0) {
        // JSON直書きのミックスカード → フォームに直接自動入力
        const mixName = data.n || data.name;
        if (mixName) setRecipeName(mixName);
        const newFlavors: FormFlavorItem[] = mixFlavors.map((fl: any, idx: number) => {
          const fid = fl.i || fl.id || fl.flavor_id || fl.flavorId || '';
          const knownFlavor = allFlavors.find((f) => f.id === fid);
          
          return {
            id: `row-${idx}-${Date.now()}`,
            isCustom: !knownFlavor, 
            flavorId: fid,
            brandName: knownFlavor ? knownFlavor.brand_name : (fl.b || fl.brand_name || fl.brandName || fl.brand || ''),
            flavorName: knownFlavor ? knownFlavor.name : (fl.v || fl.flavor_name || fl.flavorName || fl.name || ''),
            ratio: String(fl.r ?? fl.ratio ?? 0),
            searchText: knownFlavor 
              ? `${knownFlavor.brand_name} - ${knownFlavor.name}` 
              : `${fl.b || fl.brand_name || fl.brand || ''} - ${fl.v || fl.flavor_name || fl.name || ''}`,
            isDropdownOpen: false,
          };
        });
        setFlavors(newFlavors);
        showHUD({ type: 'success', message: 'ミックスカードを自動入力しました' });
        return;
      }
    } catch(e) { /* ignore */ }

    // URL形式のミックスカード判定
    const match = decodedText.match(/(?:mixes|r)\/([a-zA-Z0-9_-]+)/);
    if (!match) {
      showHUD({ type: 'error', message: '無効なQRコードです' });
      return;
    }
    const mixId = match[1];
    try {
      const mix = await MixRepository.getById(mixId);
      if (!mix) {
        showHUD({ type: 'error', message: '指定されたレシピが見つかりません' });
        return;
      }
      
      const shishaMix = mapDbMixToShishaMix(mix);
      setScannedMixData(shishaMix);
    } catch (e) {
      showHUD({ type: 'error', message: '読み込みに失敗しました' });
    }
  };

  const handleConfirmScannedMix = async () => {
    if (!scannedMixData) return;
    const mixId = scannedMixData.id;
    setScannedMixData(null);
    try {
      const mix = await MixRepository.getById(mixId);
      if (!mix) return;
      
      setRecipeName(mix.name);
      
      // Update flavors list
      const newFlavors: FormFlavorItem[] = mix.flavors.map((f, idx) => ({
        id: `row-${idx}-${Date.now()}`,
        isCustom: false,
        flavorId: f.flavor_id,
        brandName: f.brand_name,
        flavorName: f.flavor_name,
        ratio: f.ratio.toString(),
        searchText: `${f.brand_name} - ${f.flavor_name}`,
        isDropdownOpen: false,
      }));
      
      setFlavors(newFlavors);
      showHUD({ type: 'success', message: 'レシピを自動入力しました' });
    } catch (e) {
      showHUD({ type: 'error', message: '入力に失敗しました' });
    }
  };

  // Helper to calculate total ratio
  const getTotalRatio = () => {
    return flavors.reduce(
      (sum, item) => sum + (parseFloat(item.ratio) || 0),
      0,
    );
  };

  const totalRatio = getTotalRatio();
  const isRatioValid = totalRatio === 100 || totalRatio === 0;

  // Add flavor row
  const handleAddFlavor = () => {
    if (flavors.length >= 6) return;
    setFlavors([
      ...flavors,
      {
        id: `row-${Date.now()}-${Math.random()}`,
        isCustom: false,
        flavorId: '',
        brandName: '',
        flavorName: '',
        ratio: '0',
        searchText: '',
        isDropdownOpen: false,
      },
    ]);
  };

  // Remove flavor row
  const handleRemoveFlavor = (id: string) => {
    if (flavors.length <= 1) return;
    setFlavors(flavors.filter((f) => f.id !== id));
  };

  // Handle changes within a row
  const handleFlavorRowChange = (
    index: number,
    updates: Partial<FormFlavorItem>,
  ) => {
    const nextFlavors = [...flavors];
    nextFlavors[index] = { ...nextFlavors[index], ...updates };
    setFlavors(nextFlavors);
  };

  // Toggle isCustom mode for a row
  const handleToggleMode = (index: number) => {
    const row = flavors[index];
    if (row.isCustom) {
      // Toggle back to owned search mode
      handleFlavorRowChange(index, {
        isCustom: false,
        flavorId: '',
        brandName: '',
        flavorName: '',
        searchText: '',
        isDropdownOpen: false,
      });
    } else {
      // Toggle to manual entry mode
      handleFlavorRowChange(index, {
        isCustom: true,
        flavorId: '',
        brandName: '',
        flavorName: '',
        searchText: '',
        isDropdownOpen: false,
      });
    }
  };

  // Filter owned flavors based on query
  const getFilteredOwnedFlavors = (text: string) => {
    const query = text.toLowerCase().trim();
    if (!query) return ownedFlavors;
    return ownedFlavors.filter(
      (f) =>
        f.brand_name.toLowerCase().includes(query) ||
        f.name.toLowerCase().includes(query),
    );
  };

  // Auto-generate recipe name from selected flavors
  const getAutoGeneratedName = () => {
    const names = flavors
      .map((f) => {
        if (f.isCustom) {
          return f.flavorName.trim();
        }
        const found = allFlavors.find((x) => x.id === f.flavorId);
        return found ? found.name : '';
      })
      .filter(Boolean);
    return names.length > 0 ? names.join(' × ') : '未命名レシピ';
  };

  const autoGeneratedName = getAutoGeneratedName();

  // Clear all inputs and reset flavor rows
  const handleClearAll = () => {
    setRecipeName('');
    setFlavors(
      Array.from({ length: 4 }).map((_, idx) => ({
        id: `row-${idx}-${Date.now() + idx}`,
        isCustom: false,
        flavorId: '',
        brandName: '',
        flavorName: '',
        ratio: '0',
        searchText: '',
        isDropdownOpen: false,
      })),
    );
    showHUD({ type: 'success', message: '入力を一括クリアしました' });
  };

  // Save submit triggers confirmation modal
  const handleSaveClick = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isRatioValid) {
      showHUD({ type: 'error', message: '比率の合計を100%にしてください' });
      return;
    }
    setShowSaveModal(true);
  };

  // Confirm save in modal
  const handleSaveConfirm = async () => {
    const resolvedFlavorsData: { flavor_id: string; custom_flavor_name?: string; ratio: number }[] = [];

    for (const f of flavors) {
      let fId = '';
      let cName: string | undefined = undefined;
      if (!f.isCustom && f.flavorId) {
        fId = f.flavorId;
      } else {
        // Custom text entry
        const flName = f.flavorName.trim() || 'アンネームドフレーバー';
        cName = f.brandName.trim() ? `${f.brandName.trim()} - ${flName}` : flName;
      }
      resolvedFlavorsData.push({
        flavor_id: fId,
        custom_flavor_name: cName,
        ratio: parseFloat(f.ratio) || 0,
      });
    }

    const finalName = recipeName.trim() || flavors.map(f => f.flavorName.trim() || 'アンネームド').join(' × ');

    const mixData = {
      name: finalName,
      impression: 'レシピ作成画面から作成されました。',
      rating: 5,
      is_public: false,
    };

    await MixRepository.save(mixData, resolvedFlavorsData, editingMixId || undefined);
    showHUD({ type: 'success', message: 'レシピを保存しました' });
    setShowSaveModal(false);
    navigate('/recipes');
  };

  // Helper to generate a random mix from a given flavor pool
  const generateRandomMix = (pool: FlavorDetails[], isOwned: boolean) => {
    const requiredIds = flavors.filter((f) => f.isRequired && f.flavorId).map((f) => f.flavorId);
    const excludedIds = flavors.filter((f) => f.isExcluded && f.flavorId).map((f) => f.flavorId);

    const requiredFlavors = requiredIds
      .map((id) => allFlavors.find((f) => f.id === id))
      .filter(Boolean) as FlavorDetails[];

    const availablePool = pool.filter(
      (p) => !excludedIds.includes(p.id) && !requiredIds.includes(p.id)
    );

    const maxSlots = 6 - excludedIds.length;
    const minCount = Math.max(2, requiredFlavors.length);
    const maxCount = Math.min(maxSlots, availablePool.length + requiredFlavors.length);

    if (maxCount < minCount || availablePool.length + requiredFlavors.length < 2) {
      showHUD({ type: 'error', message: '対象フレーバーが不足しているか、必須・除外の指定が多すぎます' });
      return;
    }

    if (isFirstVisit) {
      localStorage.setItem('hf_has_visited_create_recipe', 'true');
      setIsFirstVisit(false);
    }
    setLastRandomPool(isOwned ? 'owned' : 'all');

    const count =
      Math.floor(Math.random() * (maxCount - minCount + 1)) + minCount;

    const pickCount = count - requiredFlavors.length;
    const shuffled = [...availablePool].sort(() => 0.5 - Math.random());
    const selected = [...requiredFlavors, ...shuffled.slice(0, pickCount)];
    selected.sort(() => 0.5 - Math.random());

    // Generate random ratios summing to 100% in steps of 5%
    const ratios: number[] = [];
    let remaining = 100;

    for (let i = 0; i < count - 1; i++) {
      const min = 10;
      const max = remaining - (count - 1 - i) * 10;
      if (max <= min) {
        ratios.push(min);
        remaining -= min;
        continue;
      }
      const step = 5;
      const range = (max - min) / step;
      const val = min + Math.floor(Math.random() * (range + 1)) * step;
      ratios.push(val);
      remaining -= val;
    }
    ratios.push(remaining);
    ratios.sort((a, b) => b - a);

    const result = selected.map((f, idx) => ({
      id: f.id,
      brand_name: f.brand_name,
      name: f.name,
      ratio: ratios[idx],
      color: getFlavorColor(f.brand_name, f.name),
      taste_vector: f.taste_vector,
    }));

    setRandomFlavors(result);

    // Calculate combined weighted taste vector
    const combined = {
      sweetness: 0,
      sourness: 0,
      coolness: 0,
      fruitiness: 0,
    };

    result.forEach((rf) => {
      const weight = rf.ratio / 100;
      combined.sweetness += rf.taste_vector.sweetness * weight;
      combined.sourness += rf.taste_vector.sourness * weight;
      combined.coolness += rf.taste_vector.coolness * weight;
      combined.fruitiness += rf.taste_vector.fruitiness * weight;
    });

    const roundVal = (v: number) => Math.round(v * 10) / 10;
    setRandomTasteVector({
      sweetness: roundVal(combined.sweetness),
      sourness: roundVal(combined.sourness),
      coolness: roundVal(combined.coolness),
      fruitiness: roundVal(combined.fruitiness),
    });

    // Also set the main form's flavors ("フレーバー選択欄にセットする")
    const newFlavors: FormFlavorItem[] = result.map((rf, idx) => ({
      id: `row-${idx}-${Date.now()}`,
      isCustom: false,
      flavorId: rf.id,
      brandName: rf.brand_name,
      flavorName: rf.name,
      ratio: rf.ratio.toString(),
      searchText: `${rf.brand_name} - ${rf.name}`,
      isDropdownOpen: false,
      isRequired: requiredIds.includes(rf.id),
      isExcluded: false,
    }));

    // Re-append the excluded flavors so they remain in the form
    const excludedFormItems = flavors.filter((f) => f.isExcluded && f.flavorId);
    excludedFormItems.forEach((f, idx) => {
      newFlavors.push({
        ...f,
        id: `row-excl-${idx}-${Date.now()}`,
      });
    });

    setFlavors(newFlavors.slice(0, 6));

    setShowRandomModal(true);
  };

  // Generate mix from OWNED flavors
  const handleSuggestGenerate = () => {
    generateRandomMix(ownedFlavors, true);
  };

  // Generate mix from ALL flavors
  const handleRandomGenerate = () => {
    generateRandomMix(allFlavors, false);
  };

  // Reroll based on last used pool
  const handleReroll = () => {
    if (lastRandomPool === 'owned') {
      generateRandomMix(ownedFlavors, true);
    } else {
      generateRandomMix(allFlavors, false);
    }
  };

  // Save generated random mix directly
  const handleSaveRandomConfirm = async () => {
    const resolvedFlavorsData = randomFlavors.map((rf) => ({
      flavor_id: rf.id,
      ratio: rf.ratio,
    }));

    const mixName =
      recipeName.trim() || randomFlavors.map((rf) => rf.name).join(' × ');

    const mixData = {
      name: mixName,
      impression: 'ランダム自動生成によって作成されたミックス。',
      rating: 5,
      is_public: false,
    };

    await MixRepository.save(mixData, resolvedFlavorsData);
    showHUD({ type: 'success', message: 'レシピを保存しました' });
    setShowRandomModal(false);
    navigate('/recipes');
  };

  // Styles
  const containerStyle: React.CSSProperties = {
    padding: '40px 20px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '30px',
    animation: 'fadeIn 0.5s ease-out',
    maxWidth: '800px',
    margin: '0 auto',
    position: 'relative',
  };

  const headerRowStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'flex-start',
    width: '100%',
    marginBottom: '10px',
  };

  const titleStyle: React.CSSProperties = {
    fontFamily: 'var(--font-heading-ja)',
    fontSize: 'var(--font-size-h1)',
    color: 'var(--color-text)',
    margin: 0,
    alignSelf: 'flex-start',
  };

  const formStyle: React.CSSProperties = {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '30px',
  };

  const formSectionStyle: React.CSSProperties = {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  };

  const inputNameStyle: React.CSSProperties = {
    width: '100%',
    padding: '16px 20px',
    backgroundColor: 'var(--color-surface)',
    border: '2px solid rgba(231, 228, 223, 0.1)',
    borderRadius: '12px',
    fontSize: 'var(--font-size-body)',
    color: 'var(--color-text)',
    outline: 'none',
    transition: 'border-color 0.2s',
    textAlign: 'center',
  };

  const rowCardStyle: React.CSSProperties = {
    padding: '16px 20px',
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '15px',
    backgroundColor: 'rgba(38, 40, 48, 0.6)',
    border: '1px solid rgba(231, 228, 223, 0.08)',
    borderRadius: '12px',
    position: 'relative',
    transition: 'all 0.2s ease-out',
  };

  const searchContainerStyle: React.CSSProperties = {
    flex: 1,
    minWidth: '200px',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  };

  const autocompleteInputStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px 16px',
    backgroundColor: 'var(--color-background)',
    border: '1px solid rgba(231, 228, 223, 0.1)',
    borderRadius: '8px',
    color: 'var(--color-text)',
    outline: 'none',
    fontSize: 'var(--font-size-body)',
  };

  const toggleLinkStyle: React.CSSProperties = {
    fontSize: '12px',
    color: 'var(--color-accent)',
    cursor: 'pointer',
    opacity: 0.8,
    textDecoration: 'underline',
    whiteSpace: 'nowrap',
  };

  const dropdownListStyle: React.CSSProperties = {
    position: 'absolute',
    top: '48px',
    left: 0,
    right: 0,
    backgroundColor: 'var(--color-surface)',
    border: '1px solid rgba(231, 228, 223, 0.15)',
    borderRadius: '8px',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)',
    maxHeight: '200px',
    overflowY: 'auto',
    zIndex: 50,
  };

  const dropdownItemStyle: React.CSSProperties = {
    padding: '10px 15px',
    cursor: 'pointer',
    fontSize: 'var(--font-size-caption)',
    borderBottom: '1px solid rgba(231, 228, 223, 0.05)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  };

  const brandSelectStyle: React.CSSProperties = {
    padding: '12px',
    backgroundColor: 'var(--color-surface)',
    border: '1px solid rgba(231, 228, 223, 0.1)',
    borderRadius: '12px',
    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
    color: 'var(--color-text)',
    fontSize: 'var(--font-size-body)',
    flex: '1 1 120px',
    minWidth: '120px',
    maxWidth: '100%',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  };

  const flavorInputStyle: React.CSSProperties = {
    flex: '2 1 140px',
    padding: '12px 16px',
    backgroundColor: 'var(--color-surface)',
    border: '1px solid rgba(231, 228, 223, 0.1)',
    borderRadius: '12px',
    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
    color: 'var(--color-text)',
    fontSize: 'var(--font-size-body)',
    outline: 'none',
    minWidth: '140px',
    maxWidth: '100%',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  };

  const ratioWrapperStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: 'var(--color-background)',
    border: '1px solid rgba(231, 228, 223, 0.1)',
    borderRadius: '8px',
    padding: '0 12px',
    width: '90px',
  };

  const ratioInputStyle: React.CSSProperties = {
    width: '100%',
    border: 'none',
    backgroundColor: 'transparent',
    color: 'var(--color-text)',
    fontSize: 'var(--font-size-body)',
    textAlign: 'right',
    padding: '12px 0',
    outline: 'none',
  };

  const deleteBtnStyle: React.CSSProperties = {
    padding: '10px',
    color: 'var(--color-error)',
    opacity: 0.7,
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const addRowBtnStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    backgroundColor: 'transparent',
    border: '1px dashed var(--color-accent)',
    color: 'var(--color-accent)',
    padding: '12px 30px',
    borderRadius: '12px',
    fontSize: 'var(--font-size-body)',
    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.15)',
    width: '100%',
    cursor: 'pointer',
  };

  const disabledAddRowBtnStyle: React.CSSProperties = {
    ...addRowBtnStyle,
    opacity: 0.4,
    cursor: 'not-allowed',
    border: '1px dashed rgba(231, 228, 223, 0.2)',
    color: 'var(--color-text)',
  };

  const clearBtnStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    backgroundColor: 'transparent',
    border: '1px solid rgba(212, 106, 106, 0.4)',
    color: 'var(--color-error)',
    padding: '12px 30px',
    borderRadius: '12px',
    fontSize: 'var(--font-size-body)',
    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.15)',
    width: '100%',
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
  };

  const validationBoxStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px',
    borderRadius: '8px',
    fontSize: 'var(--font-size-caption)',
    textAlign: 'center',
    fontWeight: 'bold',
    transition: 'all 0.3s ease',
  };

  const saveBtnStyle: React.CSSProperties = {
    width: '100%',
    padding: '16px',
    backgroundColor: 'var(--color-primary)',
    color: 'var(--color-text)',
    borderRadius: '12px',
    fontWeight: 'bold',
    fontSize: 'var(--font-size-body)',
    boxShadow: '0 4px 15px rgba(107, 78, 255, 0.3)',
    cursor: 'pointer',
  };

  const disabledSaveBtnStyle: React.CSSProperties = {
    ...saveBtnStyle,
    backgroundColor: 'var(--color-surface)',
    color: 'rgba(231, 228, 223, 0.4)',
    boxShadow: 'none',
    cursor: 'not-allowed',
    border: '1px solid rgba(231, 228, 223, 0.1)',
  };

  const listLinkStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: 'var(--font-size-caption)',
    opacity: 0.7,
    marginTop: '10px',
    cursor: 'pointer',
  };

  const modalOverlayStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(26, 28, 31, 0.65)',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 2000,
  };

  const modalContentStyle: React.CSSProperties = {
    padding: '30px',
    width: '460px',
    maxWidth: '90%',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    alignItems: 'center',
    backgroundColor: 'rgba(38, 40, 48, 0.85)',
    border: '1px solid rgba(231, 228, 223, 0.15)',
    borderRadius: '16px',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.5)',
    animation: 'fadeIn 0.25s ease-out',
  };

  const confirmFlavorsListStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    width: '100%',
    backgroundColor: 'rgba(26, 28, 31, 0.4)',
    padding: '15px',
    borderRadius: '10px',
    border: '1px solid rgba(231, 228, 223, 0.05)',
  };

  const cancelButtonStyle: React.CSSProperties = {
    flex: 1,
    padding: '12px',
    backgroundColor: 'transparent',
    border: '1px solid rgba(231, 228, 223, 0.2)',
    color: 'var(--color-text)',
    borderRadius: '10px',
    fontSize: 'var(--font-size-caption)',
  };

  const confirmSaveButtonStyle: React.CSSProperties = {
    flex: 1,
    padding: '12px',
    backgroundColor: 'var(--color-primary)',
    color: 'var(--color-text)',
    borderRadius: '10px',
    fontSize: 'var(--font-size-caption)',
    fontWeight: 'bold',
  };

  return (
    <div style={containerStyle}>
      <div style={headerRowStyle}>
        <h1 style={titleStyle}>レシピ作成</h1>
        <button
          type="button"
          onClick={() => {
            if (sessionStorage.getItem('camera_permission_granted') === 'true') {
              setShowScanner(true);
            } else {
              setShowPermissionModal(true);
            }
          }}
          style={{
            marginLeft: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            backgroundColor: 'var(--color-surface)',
            border: '1px solid rgba(231, 228, 223, 0.2)',
            borderRadius: '8px',
            color: 'var(--color-text)',
            cursor: 'pointer',
            fontSize: 'var(--font-size-caption)',
            transition: 'background-color 0.2s',
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(231, 228, 223, 0.1)'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--color-surface)'}
        >
          <Camera size={18} />
          QRスキャン
        </button>
      </div>

      <form onSubmit={handleSaveClick} style={formStyle}>
        {/* Recipe name input */}
        <input
          type="text"
          placeholder="レシピ名 (未入力の場合はフレーバー構成から自動生成)"
          value={recipeName}
          onChange={(e) => setRecipeName(e.target.value)}
          style={inputNameStyle}
          onFocus={(e) =>
            (e.currentTarget.style.borderColor = 'var(--color-primary)')
          }
          onBlur={(e) =>
            (e.currentTarget.style.borderColor = 'rgba(231, 228, 223, 0.1)')
          }
        />

        {/* Flavor rows stack */}
        <div style={formSectionStyle}>
          {flavors.map((row, index) => {
            const matchedFlavors = getFilteredOwnedFlavors(row.searchText);

            return (
              <div
                key={row.id}
                style={rowCardStyle}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor =
                    'rgba(231, 228, 223, 0.2)';
                  e.currentTarget.style.backgroundColor =
                    'rgba(38, 40, 48, 0.8)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor =
                    'rgba(231, 228, 223, 0.08)';
                  e.currentTarget.style.backgroundColor =
                    'rgba(38, 40, 48, 0.6)';
                }}
              >
                {/* Mode toggle and input */}
                {!row.isCustom ? (
                  /* Mode 1: Owned Autocomplete */
                  <div style={searchContainerStyle}>
                    <input
                      type="text"
                      placeholder="所持フレーバーから検索..."
                      value={row.searchText}
                      onFocus={() =>
                        handleFlavorRowChange(index, { isDropdownOpen: true })
                      }
                      onChange={(e) =>
                        handleFlavorRowChange(index, {
                          searchText: e.target.value,
                          flavorId: '',
                          isDropdownOpen: true,
                        })
                      }
                      style={autocompleteInputStyle}
                    />
                    <button
                      type="button"
                      onClick={() => handleToggleMode(index)}
                      style={{
                        padding: '8px',
                        backgroundColor: 'transparent',
                        border: '1px dashed rgba(231, 228, 223, 0.2)',
                        color: 'var(--color-text)',
                        borderRadius: '8px',
                        fontSize: '12px',
                        cursor: 'pointer',
                        width: '100%',
                        transition: 'all 0.2s',
                        opacity: 0.8,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(231, 228, 223, 0.05)';
                        e.currentTarget.style.opacity = '1';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.opacity = '0.8';
                      }}
                    >
                      所持外フレーバーを直接入力
                    </button>

                    {/* Autocomplete Dropdown list */}
                    {row.isDropdownOpen && (
                      <>
                        <div
                          style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            zIndex: 40,
                          }}
                          onClick={() =>
                            handleFlavorRowChange(index, {
                              isDropdownOpen: false,
                            })
                          }
                        />
                        <div className="glass-panel" style={dropdownListStyle}>
                          {matchedFlavors.length > 0 ? (
                            matchedFlavors.map((fl) => (
                              <div
                                key={fl.id}
                                style={dropdownItemStyle}
                                onMouseEnter={(e) =>
                                  (e.currentTarget.style.backgroundColor =
                                    'rgba(107, 78, 255, 0.25)')
                                }
                                onMouseLeave={(e) =>
                                  (e.currentTarget.style.backgroundColor =
                                    'transparent')
                                }
                                onClick={() => {
                                  handleFlavorRowChange(index, {
                                    flavorId: fl.id,
                                    searchText: `${fl.brand_name} - ${fl.name}`,
                                    brandName: fl.brand_name,
                                    flavorName: fl.name,
                                    isDropdownOpen: false,
                                  });
                                }}
                              >
                                <span>
                                  {fl.brand_name} - <strong>{fl.name}</strong>
                                </span>
                                <span
                                  style={{
                                    fontSize: '11px',
                                    padding: '2px 6px',
                                    borderRadius: '4px',
                                    backgroundColor: 'rgba(201, 166, 107, 0.2)',
                                    color: 'var(--color-accent)',
                                  }}
                                >
                                  {fl.series_name || '一般'}
                                </span>
                              </div>
                            ))
                          ) : (
                            <div
                              style={{
                                padding: '15px',
                                textShadow: 'none',
                                opacity: 0.6,
                                fontSize: '12px',
                                textAlign: 'center',
                              }}
                            >
                              一致する所持フレーバーがありません。
                              <div
                                style={{
                                  marginTop: '5px',
                                  color: 'var(--color-accent)',
                                  cursor: 'pointer',
                                  textDecoration: 'underline',
                                }}
                                onClick={() => handleToggleMode(index)}
                              >
                                直接入力モードに切り替える
                              </div>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  /* Mode 2: Custom Text Entry */
                  <div
                    style={{
                      flex: 1,
                      minWidth: '200px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: '10px',
                      }}
                    >
                    <select
                      value={row.brandName}
                      onChange={(e) =>
                        handleFlavorRowChange(index, {
                          brandName: e.target.value,
                          flavorName: '',
                        })
                      }
                      style={brandSelectStyle}
                    >
                      <option value="">(ブランド未選択)</option>
                      {[...allBrands]
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((b) => (
                        <option key={b.id} value={b.name}>
                          {b.name}
                        </option>
                      ))}
                    </select>

                    {row.brandName ? (
                      <select
                        value={row.flavorName}
                        onChange={(e) =>
                          handleFlavorRowChange(index, {
                            flavorName: e.target.value,
                          })
                        }
                        style={flavorInputStyle}
                      >
                        <option value="">(フレーバーを選択)</option>
                        {allFlavors
                          .filter(
                            (x) =>
                              x.brand_name.toLowerCase() ===
                              row.brandName.toLowerCase(),
                          )
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map((x) => (
                            <option key={x.id} value={x.name}>
                              {x.name}
                            </option>
                          ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        placeholder="フレーバー名を自由入力..."
                        value={row.flavorName}
                        onChange={(e) =>
                          handleFlavorRowChange(index, {
                            flavorName: e.target.value,
                          })
                        }
                        style={flavorInputStyle}
                      />
                    )}

                    </div>
                    <button
                      type="button"
                      onClick={() => handleToggleMode(index)}
                      style={{
                        padding: '8px',
                        backgroundColor: 'transparent',
                        border: '1px dashed rgba(201, 166, 107, 0.4)',
                        color: 'var(--color-accent)',
                        borderRadius: '8px',
                        fontSize: '12px',
                        cursor: 'pointer',
                        width: '100%',
                        transition: 'all 0.2s',
                        opacity: 0.9,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(201, 166, 107, 0.05)';
                        e.currentTarget.style.opacity = '1';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.opacity = '0.9';
                      }}
                    >
                      所持フレーバーから選択へ戻る
                    </button>
                  </div>
                )}

                {/* Ratio input with permanent slider bar underneath */}
                <div
                  style={{
                    width: '100px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    alignItems: 'center',
                  }}
                >
                  <div style={{ ...ratioWrapperStyle, width: '100%', height: '42px', opacity: (row.isRequired || row.isExcluded) ? 0.5 : 1 }}>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="1"
                      placeholder="比率"
                      value={row.ratio}
                      onChange={(e) =>
                        handleFlavorRowChange(index, { ratio: e.target.value })
                      }
                      style={ratioInputStyle}
                      disabled={row.isRequired || row.isExcluded}
                    />
                    <span
                      style={{
                        fontSize: 'var(--font-size-caption)',
                        opacity: 0.6,
                        fontFamily: 'var(--font-numbers)',
                      }}
                    >
                      %
                    </span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="1"
                    value={row.ratio || '0'}
                    onChange={(e) => {
                      handleFlavorRowChange(index, { ratio: e.target.value });
                    }}
                    style={{
                      width: '100%',
                      accentColor: 'var(--color-primary)',
                      cursor: (row.isRequired || row.isExcluded) ? 'not-allowed' : 'pointer',
                      opacity: (row.isRequired || row.isExcluded) ? 0.5 : 1
                    }}
                    disabled={row.isRequired || row.isExcluded}
                  />
                  <div style={{ display: 'flex', gap: '8px', fontSize: '10px', marginTop: '2px', opacity: 0.8 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '2px', cursor: 'pointer', opacity: row.isExcluded ? 0.4 : 1 }}>
                      <input
                        type="checkbox"
                        checked={row.isRequired || false}
                        onChange={(e) => handleFlavorRowChange(index, { isRequired: e.target.checked, isExcluded: false })}
                      />
                      必須
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '2px', cursor: 'pointer', opacity: row.isRequired ? 0.4 : 1 }}>
                      <input
                        type="checkbox"
                        checked={row.isExcluded || false}
                        onChange={(e) => handleFlavorRowChange(index, { isExcluded: e.target.checked, isRequired: false, ratio: '0' })}
                      />
                      除外
                    </label>
                  </div>
                </div>

                {/* Remove button */}
                <button
                  type="button"
                  style={deleteBtnStyle}
                  onClick={() => handleRemoveFlavor(row.id)}
                  disabled={flavors.length <= 1}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.7')}
                  title="行を削除"
                >
                  <X size={18} />
                </button>
              </div>
            );
          })}
        </div>

        {/* Add button (+) */}
        <button
          type="button"
          onClick={handleAddFlavor}
          disabled={flavors.length >= 6}
          style={flavors.length >= 6 ? disabledAddRowBtnStyle : addRowBtnStyle}
          onMouseEnter={(e) => {
            if (flavors.length < 6) {
              e.currentTarget.style.backgroundColor =
                'rgba(201, 166, 107, 0.1)';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <Plus size={18} />
          フレーバーを追加 (最大6つ)
        </button>

        {/* Clear all inputs button */}
        <button
          type="button"
          onClick={handleClearAll}
          style={clearBtnStyle}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(212, 106, 106, 0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <RotateCw size={18} />
          入力を一括クリア
        </button>

        {/* Validation Box */}
        <div
          style={{
            ...validationBoxStyle,
            backgroundColor: isRatioValid
              ? 'rgba(91, 161, 139, 0.15)'
              : 'rgba(212, 106, 106, 0.15)',
            color: isRatioValid ? 'var(--color-success)' : 'var(--color-error)',
            border: `1px solid ${isRatioValid ? 'var(--color-success)' : 'var(--color-error)'}`,
          }}
        >
          {totalRatio === 0 ? (
            <span>✓ すべての比率が0%です。分量比未指定として保存されます。</span>
          ) : isRatioValid ? (
            <span>✓ 比率の合計が100%に一致しています。保存可能です。</span>
          ) : (
            <span>
              ⚠ 比率の合計: {totalRatio}%
              (保存するには合計を100%またはすべて0%にする必要があります)
            </span>
          )}
        </div>

        {/* Save button */}
        <button
          type="submit"
          disabled={!isRatioValid}
          style={!isRatioValid ? disabledSaveBtnStyle : saveBtnStyle}
          onMouseEnter={(e) => {
            if (isRatioValid) {
              e.currentTarget.style.backgroundColor =
                'rgba(107, 78, 255, 0.85)';
            }
          }}
          onMouseLeave={(e) => {
            if (isRatioValid) {
              e.currentTarget.style.backgroundColor = 'var(--color-primary)';
            }
          }}
        >
          保存
        </button>
      </form>

      {/* Navigate to recipe list button */}
      <div style={listLinkStyle} onClick={() => navigate('/recipes')}>
        <ArrowLeft size={16} />
        レシピ一覧へ戻る
      </div>

      {/* 1. Save Confirmation Modal */}
      {showSaveModal && (
        <div style={modalOverlayStyle} onClick={() => setShowSaveModal(false)}>
          <div
            className="glass-panel"
            style={{
              ...modalContentStyle,
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              style={{
                fontSize: 'var(--font-size-h3)',
                fontFamily: 'var(--font-heading-ja)',
                color: 'var(--color-accent)',
                margin: 0,
              }}
            >
              レシピ保存の確認
            </h3>

            <div
              style={{ width: '100%', textAlign: 'center', marginTop: '10px' }}
            >
              <div
                style={{ fontSize: '12px', opacity: 0.6, marginBottom: '5px' }}
              >
                レシピ名
              </div>
              <div
                style={{
                  fontWeight: 'bold',
                  fontSize: '18px',
                  color: 'var(--color-text)',
                }}
              >
                {recipeName.trim() || autoGeneratedName}
              </div>
            </div>

            <div
              style={{
                width: '100%',
                borderBottom: '1px solid rgba(231, 228, 223, 0.1)',
                margin: '5px 0',
              }}
            />

            <div style={{ width: '100%' }}>
              <div
                style={{
                  fontSize: '12px',
                  opacity: 0.6,
                  marginBottom: '10px',
                  textAlign: 'center',
                }}
              >
                配合フレーバー
              </div>
              <div style={confirmFlavorsListStyle}>
                {flavors.map((f) => {
                  const bName = f.isCustom
                    ? f.brandName || 'Custom'
                    : allFlavors.find((x) => x.id === f.flavorId)?.brand_name ||
                      'Unknown';
                  const flName = f.isCustom
                    ? f.flavorName || 'アンネームド'
                    : allFlavors.find((x) => x.id === f.flavorId)?.name ||
                      'アンネームド';
                  return (
                    <div
                      key={f.id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        width: '100%',
                        fontSize: 'var(--font-size-caption)',
                      }}
                    >
                      <span style={{ opacity: 0.95 }}>
                        {bName} - {flName}
                      </span>
                      <span
                        style={{
                          fontFamily: 'var(--font-numbers)',
                          fontWeight: 'bold',
                          color: 'var(--color-accent)',
                        }}
                      >
                        {f.ratio}%
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div
              style={{
                display: 'flex',
                gap: '15px',
                width: '100%',
                marginTop: '10px',
              }}
            >
              <button
                type="button"
                style={cancelButtonStyle}
                onClick={() => setShowSaveModal(false)}
              >
                キャンセル
              </button>
              <button
                type="button"
                style={confirmSaveButtonStyle}
                onClick={handleSaveConfirm}
              >
                保存
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 2. Random Mix Glassmorphism Popup Modal */}
      {showRandomModal && (
        <div
          style={modalOverlayStyle}
          onClick={() => setShowRandomModal(false)}
        >
          <div
            className="glass-panel"
            style={{
              ...modalContentStyle,
              width: '520px',
              backgroundColor: 'rgba(38, 40, 48, 0.75)',
              position: 'relative',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setShowRandomModal(false)}
              style={{
                position: 'absolute',
                top: '15px',
                right: '15px',
                background: 'none',
                border: 'none',
                color: 'var(--color-text)',
                opacity: 0.6,
                cursor: 'pointer',
                transition: 'opacity 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '4px',
                zIndex: 10,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.6')}
              title="閉じる"
            >
              <X size={20} />
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
              <Dices size={24} color="var(--color-accent)" />
              <h3
                style={{
                  fontSize: 'var(--font-size-h2)',
                  fontFamily: 'var(--font-heading-ja)',
                  color: 'var(--color-text)',
                  margin: 0,
                }}
              >
                レシピの自動生成
              </h3>
            </div>

            <p
              style={{
                fontSize: '12px',
                opacity: 0.7,
                margin: 0,
                textAlign: 'center',
              }}
            >
              全フレーバーのマスターデータから、ランダムにブレンドを作成しました。
            </p>

            {/* Generated Mix details */}
            <div
              style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
              }}
            >
              <div
                style={{
                  fontWeight: 'bold',
                  fontSize: '15px',
                  color: 'var(--color-text)',
                  textAlign: 'center',
                }}
              >
                {randomFlavors.map((rf) => rf.name).join(' × ')}
              </div>

              <div
                style={{
                  display: 'flex',
                  gap: '20px',
                  alignItems: 'center',
                  marginTop: '10px',
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                }}
              >
                <div style={{ flex: '1 1 200px' }}>
                  <div style={confirmFlavorsListStyle}>
                    {randomFlavors.map((rf) => (
                      <div
                        key={rf.id}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          width: '100%',
                          fontSize: '13px',
                          alignItems: 'center',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                          }}
                        >
                          <div
                            style={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              backgroundColor: rf.color,
                            }}
                          />
                          <span style={{ opacity: 0.9 }}>
                            {rf.brand_name} - {rf.name}
                          </span>
                        </div>
                        <span
                          style={{
                            fontFamily: 'var(--font-numbers)',
                            fontWeight: 'bold',
                          }}
                        >
                          {rf.ratio}%
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Interactive SVG Radar chart visual */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <RadarChart
                    sweetness={randomTasteVector.sweetness}
                    sourness={randomTasteVector.sourness}
                    coolness={randomTasteVector.coolness}
                    fruitiness={randomTasteVector.fruitiness}
                    size={160}
                  />
                </div>
              </div>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gridAutoRows: '1fr',
                gap: '15px',
                width: '100%',
                marginTop: '10px',
              }}
            >
              <button
                type="button"
                style={{
                  ...cancelButtonStyle,
                  whiteSpace: 'normal',
                  fontSize: '12px',
                  lineHeight: '1.4',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onClick={handleSuggestGenerate}
              >
                <span>所持済みから</span>
                <span>サジェスト</span>
              </button>
              <button
                type="button"
                style={{
                  ...cancelButtonStyle,
                  whiteSpace: 'normal',
                  fontSize: '12px',
                  lineHeight: '1.4',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onClick={handleRandomGenerate}
              >
                ランダム生成
              </button>
              <button
                type="button"
                style={{
                  ...cancelButtonStyle,
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  fontSize: '12px',
                  height: '100%',
                }}
                onClick={handleReroll}
              >
                <RotateCw size={16} />
                リロール
              </button>
              <button
                type="button"
                style={{
                  ...confirmSaveButtonStyle,
                  backgroundColor: 'var(--color-primary)',
                  color: 'var(--color-text)',
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  height: '100%',
                }}
                onClick={handleSaveRandomConfirm}
              >
                保存
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating Random Button */}
      {createPortal(
        <div
        style={{
          position: 'fixed',
          bottom: '90px',
          right: '30px',
          display: 'flex',
          flexDirection: 'column',
          gap: '15px',
          zIndex: 999,
        }}
      >
        {/* Random Generate Button */}
        <button
          type="button"
          onClick={handleRandomGenerate}
          className={isFirstVisit ? 'pulse-button' : ''}
          style={{
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            backgroundColor: 'var(--color-accent)',
            color: 'var(--color-background)',
            border: 'none',
            boxShadow: isFirstVisit ? undefined : '0 4px 20px rgba(0, 0, 0, 0.4)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: isFirstVisit ? 'none' : 'all 0.2s ease-in-out',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.1) rotate(15deg)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1) rotate(0deg)';
          }}
          title="全フレーバーからランダム自動生成"
        >
          <Dices size={28} />
        </button>
      </div>,
      document.body
      )}

      {/* QR Scanner Modal */}
      {showScanner && (
        <QrScannerModal
          onScan={handleScan}
          onClose={() => setShowScanner(false)}
        />
      )}

      {/* Camera Permission Modal */}
      {showPermissionModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(26, 28, 31, 0.85)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 3000,
            animation: 'fadeIn 0.2s ease-out',
            padding: '20px',
          }}
        >
          <div
            className="glass-panel"
            style={{
              width: '90%',
              maxWidth: '400px',
              backgroundColor: 'var(--color-surface)',
              padding: '24px',
              display: 'flex',
              flexDirection: 'column',
              gap: '15px',
              borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
              textAlign: 'center',
            }}
          >
            <h3
              style={{
                margin: 0,
                color: 'var(--color-accent)',
                fontSize: '18px',
                fontFamily: 'var(--font-heading-ja)',
              }}
            >
              カメラのアクセス許可
            </h3>
            <p
              style={{
                color: 'var(--color-text)',
                fontSize: '14px',
                lineHeight: '1.6',
                margin: '10px 0',
              }}
            >
              QRコードをスキャンするためにカメラを起動します。
              <br />
              アクセスを許可しますか？
            </p>
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '12px',
                marginTop: '10px',
              }}
            >
              <button
                type="button"
                onClick={() => setShowPermissionModal(false)}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  backgroundColor: 'transparent',
                  border: '1px solid rgba(231, 228, 223, 0.1)',
                  color: 'var(--color-text)',
                  fontSize: '14px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
              >
                キャンセル
              </button>
              <button
                type="button"
                onClick={() => {
                  sessionStorage.setItem('camera_permission_granted', 'true');
                  setShowPermissionModal(false);
                  setShowScanner(true);
                }}
                style={{
                  padding: '10px 24px',
                  borderRadius: '8px',
                  backgroundColor: 'var(--color-primary)',
                  color: 'var(--color-text)',
                  fontSize: '14px',
                  border: 'none',
                  cursor: 'pointer',
                  boxShadow: '0 4px 15px rgba(107, 78, 255, 0.3)',
                  transition: 'all 0.2s',
                }}
              >
                許可する
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Scanned Mix Card Confirmation */}
      {scannedMixData && (
        <RecipeCardPopup
          mix={scannedMixData}
          onClose={() => setScannedMixData(null)}
          onConfirm={handleConfirmScannedMix}
          confirmText="このレシピを入力"
        />
      )}
    </div>
  );
}
